import { useState, useEffect } from 'react'
import { ArrowLeft, Building2, FileText, Handshake, Users, ShieldCheck, ShieldX, Clock, Check, X, Mail, DollarSign } from 'lucide-react'
import { documentsApi, agentRequestsApi, accessRequestsApi, companiesVerificationApi, subscriptionsApi } from '../lib/api'
import { formatCurrency, formatDate } from '../lib/format'

const docStatusBadge = (s) => {
  const map = {
    Pending: 'border-amber-300 text-amber-700 bg-amber-50',
    missing: 'border-neutral-300 text-neutral-600 bg-neutral-50',
    uploaded: 'border-blue-300 text-blue-700 bg-blue-50',
    awaiting: 'border-violet-300 text-violet-700 bg-violet-50',
    Verified: 'border-emerald-300 text-emerald-700 bg-emerald-50',
    Rejected: 'border-rose-300 text-rose-700 bg-rose-50',
  }
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${map[s] || 'border-neutral-300 text-neutral-600 bg-neutral-50'}`}>{s}</span>
}

const agentStatusBadge = (s) => {
  const map = {
    Assigned: 'border-amber-300 text-amber-700 bg-amber-50',
    Drafting: 'border-blue-300 text-blue-700 bg-blue-50',
    'Draft Ready': 'border-violet-300 text-violet-700 bg-violet-50',
    'Client Review': 'border-cyan-300 text-cyan-700 bg-cyan-50',
    Finalized: 'border-emerald-300 text-emerald-700 bg-emerald-50',
  }
  return <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${map[s] || 'border-neutral-300 text-neutral-600 bg-neutral-50'}`}>{s}</span>
}

export default function CompanyDetail({ company, onBack }) {
  const [docs, setDocs] = useState([])
  const [requests, setRequests] = useState([])
  const [investors, setInvestors] = useState([])
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [verifyLoading, setVerifyLoading] = useState(false)

  useEffect(() => {
    if (!company?.id) return
    let mounted = true
    ;(async () => {
      const [docsRes, reqRes, invRes, subRes] = await Promise.all([
        documentsApi.list(company.id),
        agentRequestsApi.list(),
        accessRequestsApi.listAll(),
        subscriptionsApi.get(company.id),
      ])
      if (!mounted) return
      if (!docsRes.error && docsRes.data) setDocs(docsRes.data)
      if (!reqRes.error && reqRes.data) setRequests(reqRes.data.filter((r) => r.company_id === company.id))
      if (!invRes.error && invRes.data) setInvestors(invRes.data.filter((r) => r.company_id === company.id))
      if (!subRes.error && subRes.data) setSubscription(subRes.data)
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [company?.id])

  const handleVerify = async (status) => {
    setVerifyLoading(true)
    await companiesVerificationApi.setStatus(company.id, status)
    setVerifyLoading(false)
    company.verification_status = status
  }

  const verificationStatus = company?.verification_status || 'unverified'

  return (
    <div className="p-8 lg:p-10 space-y-8 animate-fade-in">
      <button onClick={onBack} className="btn-ghost text-xs flex items-center gap-1.5">
        <ArrowLeft size={14} /> Back to Companies
      </button>

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-brand-soft flex items-center justify-center">
            <Building2 size={22} className="text-brand" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">{company?.name || 'Unnamed Company'}</h1>
            <p className="text-sm text-muted mt-1">
              {company?.country || '—'} · {company?.industry || '—'} · Plan: <span className="capitalize">{company?.subscription_tier || 'seed'}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {verificationStatus === 'verified' ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 border border-emerald-300 bg-emerald-50">
              <ShieldCheck size={14} /> Verified
            </span>
          ) : (
            <button
              onClick={() => handleVerify('verified')}
              disabled={verifyLoading}
              className="btn-primary text-xs flex items-center gap-1.5"
            >
              <ShieldCheck size={14} /> {verifyLoading ? 'Verifying…' : 'Verify Company'}
            </button>
          )}
        </div>
      </div>

      {verificationStatus === 'unverified' && (
        <div className="border border-amber-200 bg-amber-50 px-5 py-3 flex items-center gap-2">
          <ShieldX size={15} className="text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800">
            This company is unverified. The founder can add documents but cannot transact until you verify their business.
          </p>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          <div className="surface h-32 animate-pulse" />
          <div className="surface h-32 animate-pulse" />
        </div>
      ) : (
        <>
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4 flex items-center gap-2">
              <FileText size={14} /> Documents ({docs.length})
            </h2>
            <div className="surface divide-y divide-neutral-200">
              {docs.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-muted">No documents uploaded yet.</div>
              ) : (
                docs.map((d) => (
                  <div key={d.id} className="grid grid-cols-12 px-6 py-3 text-sm items-center">
                    <div className="col-span-5 text-ink">{d.label}</div>
                    <div className="col-span-3 text-xs text-neutral-600">{d.file_name || 'No file'}</div>
                    <div className="col-span-2">{docStatusBadge(d.status)}</div>
                    <div className="col-span-2 font-mono text-xs text-neutral-500 text-right">{formatDate(d.created_at, 'DD/MM/YYYY')}</div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4 flex items-center gap-2">
              <Handshake size={14} /> Agent Requests ({requests.length})
            </h2>
            <div className="surface divide-y divide-neutral-200">
              {requests.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-muted">No agent requests submitted.</div>
              ) : (
                requests.map((r) => (
                  <div key={r.id} className="grid grid-cols-12 px-6 py-3 text-sm items-center">
                    <div className="col-span-4 text-ink">{r.document_name}</div>
                    <div className="col-span-3 text-neutral-600">{r.lawyers?.name || 'Unassigned'}</div>
                    <div className="col-span-2">{agentStatusBadge(r.status)}</div>
                    <div className="col-span-3 font-mono text-xs text-neutral-500 text-right">{formatDate(r.assigned_date, 'DD/MM/YYYY')}</div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4 flex items-center gap-2">
              <Users size={14} /> Investor Access Requests ({investors.length})
            </h2>
            <div className="surface divide-y divide-neutral-200">
              {investors.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-muted">No investor access requests.</div>
              ) : (
                investors.map((inv) => (
                  <div key={inv.id} className="grid grid-cols-12 px-6 py-3 text-sm items-center">
                    <div className="col-span-3 text-ink">{inv.investor_name}</div>
                    <div className="col-span-3 text-neutral-600">{inv.investor_fund || 'Independent'}</div>
                    <div className="col-span-3 text-xs text-neutral-600">{inv.investor_email}</div>
                    <div className="col-span-2">{docStatusBadge(inv.status)}</div>
                    <div className="col-span-1 font-mono text-xs text-neutral-500 text-right">{formatDate(inv.created_at, 'DD/MM/YYYY')}</div>
                  </div>
                ))
              )}
            </div>
          </section>

          {subscription && (
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4 flex items-center gap-2">
                <DollarSign size={14} /> Subscription
              </h2>
              <div className="surface p-6 grid grid-cols-2 sm:grid-cols-4 gap-6">
                <div>
                  <p className="text-xs text-muted">Plan</p>
                  <p className="text-sm font-semibold text-ink capitalize">{subscription.plan}</p>
                </div>
                <div>
                  <p className="text-xs text-muted">Status</p>
                  <p className="text-sm font-semibold text-ink capitalize">{subscription.status}</p>
                </div>
                <div>
                  <p className="text-xs text-muted">Renewal Date</p>
                  <p className="text-sm font-semibold text-ink font-mono">{subscription.renewal_date ? formatDate(subscription.renewal_date, 'DD/MM/YYYY') : '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted">Auto-Renew</p>
                  <p className="text-sm font-semibold text-ink">{subscription.renewal_enabled ? 'Enabled' : 'Disabled'}</p>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}
